import {
  affixCount,
  combatAffixCount,
  NON_COMBAT_AFFIX_STATS,
  AFFIX_POOLS,
  AFFIX_TIER_GROWTH,
  AFFIX_TIER_RANGES,
  HEADLINE_STATS,
  ITEM_TEMPLATES,
  PASSIVES,
  RARITY_MULT,
  RARITY_RANK,
  PACK_SPACE,
  isSlotLocked,
  itemTemplate,
  itemTier,
  TIER_LEVELS,
  tierForLevel,
  type AffixPoolEntry,
  type ItemTemplate,
} from './content/items';
import { Rng, clamp } from './rng';
import { relicShardsFromDismantle } from './items/relics';
import { ACCESSORY_DROP_WEIGHT, keepSlotShares } from './items/dropRates';
import { SIGNATURE_PASSIVE_ID, rollsSignature } from './items/perks';
import { UPGRADE_RULES, affixUpgradeMult, tierUpRefund, upgradeRefund, upgradedBase } from './items/upgrade';
import { CRAFTED_SALVAGE, craftedScrapCap, salvageMaterials } from './items/salvage';
import { RECIPE_RULES, asCraftRarity, gearCraftCost } from './items/craftCost';
import { RECALIBRATE_REFUND, REWORK_RULES } from './items/reworkRules';
import { BROKEN_REPAIR, BROKEN_RULES } from './items/durability';
import { ATTRS, type GearSlot, type ItemAffix, type ItemInstance, type ItemStatKey, type ItemStats, type Rarity, type SetId, type StackMap } from './types';

export const MAX_DURABILITY = 100;


const WHOLE: ItemStatKey[] = [...HEADLINE_STATS, 'str', 'agi', 'int', 'vit', 'per'];

function round(n: number, key: ItemStatKey): number {
  if (WHOLE.includes(key)) return Math.round(n);
  return Math.round(n * 100) / 100;
}
/** An affix value rounded as the dice round it (whole for stats, hundredths for shares). */
export const roundAffix = round;

/** 7. 10. 2026: whether the dice roll this stat in whole numbers (attributes, the headline stats). */
export function isWholeStat(k: ItemStatKey): boolean {
  return WHOLE.includes(k);
}

/**
 * Base stats of a template at a rarity. The headline stat (damage, defense,
 * capacity) is the catalog value × the rarity multiplier (Common ×1.00 …
 * Legendary ×1.70); it does not grow with item level, the tier already does that.
 * Other positive stats scale with rarity too, penalties stay as they are.
 */
export function scaledBase(t: ItemTemplate, _ilvl: number, rarity: Rarity): ItemStats {
  const out: ItemStats = {};
  const mult = RARITY_MULT[rarity];
  for (const [k, v] of Object.entries(t.base) as [ItemStatKey, number][]) {
    const val = v >= 0 ? v * mult : v;
    out[k] = round(val, k);
  }
  return out;
}

/** Growth of the affixes the catalog does not list yet: ×1 at level 1 up to ×1.5 at level 20. */
export function affixGrowth(ilvl: number): number {
  return 1 + (0.5 * (clamp(ilvl, 1, 20) - 1)) / 19;
}

/** Roll multiplier of the affixes the catalog does not list yet: Uncommon ×1.0 … Legendary ×1.3. */
export function affixRarityFactor(rarity: Rarity): number {
  return 0.9 + 0.1 * RARITY_RANK[rarity];
}

/** Catalog range of an affix at an item tier: T1 and T4 from the catalog, T2 and T3 evenly between. */
export function affixTierRange(stat: ItemStatKey, tier: 1 | 2 | 3 | 4): [number, number] | null {
  const r = AFFIX_TIER_RANGES[stat];
  if (!r) return null;
  const f = (tier - 1) / 3;
  return [r.t1[0] + (r.t4[0] - r.t1[0]) * f, r.t1[1] + (r.t4[1] - r.t1[1]) * f];
}

/** Growth of a catalog affix inside its tier (plan v0.6, A1): ×1 at the tier's lowest level, ×1.15 at its highest. */
export function affixTierFactor(ilvl: number, tier: 1 | 2 | 3 | 4): number {
  const [lo, hi] = TIER_LEVELS[tier];
  return 1 + AFFIX_TIER_GROWTH * clamp((ilvl - lo) / (hi - lo), 0, 1);
}

/**
 * Lowest and highest value an affix can roll on an item (before rounding). Catalog affixes
 * use the item's tier range and grow at most ×1.15 inside it; the others keep the old
 * level and rarity growth. `tier`: the item's own tier (C4), when it has one.
 */
export function affixRange(templateId: string, stat: ItemStatKey, ilvl: number, rarity: Rarity, tier?: number | null): [number, number] | null {
  const t = itemTemplate(templateId);
  const e = AFFIX_POOLS[t.slot].find(([k]) => k === stat);
  if (!e) return null;
  const n = (tier as 1 | 2 | 3 | 4 | null | undefined) ?? t.tier;
  const tiered = affixTierRange(stat, n);
  if (tiered) {
    const m = affixTierFactor(ilvl, n);
    return [tiered[0] * m, tiered[1] * m];
  }
  const m = affixGrowth(ilvl) * affixRarityFactor(rarity);
  return [e[1] * m, e[2] * m];
}

/**
 * 8. 10. 2026 (owner: rarer high rolls, as in Path of Exile): the distribution of an affix's roll quality (0 = the
 * range's bottom, 1 = its top). `C` keeps the uniform chances below 60%, makes 60–80% 1.5× as likely and the top rare
 * (80% or more 10%, 90% or more 4%, 95% or more 1.5%; mean 47.9%); `A` is flat below 80% (1.125×) with the same top
 * (mean 44.9%). 9. 10. 2026 (owner): `C` is the game's rule, with the 4th non-combat affix on a Legendary; simulations
 * can set another (`AFFIX_ROLL=uniform` for the rule before). The points are [quality, chance below].
 */
export const AFFIX_ROLL_RULES = { dist: 'C' as 'uniform' | 'A' | 'C' };
const ROLL_CDF: Record<'A' | 'C', [number, number][]> = {
  C: [[0, 0], [0.6, 0.6], [0.8, 0.9], [0.9, 0.96], [0.95, 0.985], [1, 1]],
  A: [[0, 0], [0.8, 0.9], [0.9, 0.96], [0.95, 0.985], [1, 1]],
};

export type AffixRollDist = 'uniform' | 'A' | 'C';

/**
 * A roll quality from a uniform draw `u` (the inverse of the distribution, piecewise linear). `dist`: a distribution of
 * its own (11. 10. 2026: Recalibrate always rolls by C, whatever a simulation sets for the game's rolls).
 */
export function rollQuality(u: number, dist: AffixRollDist = AFFIX_ROLL_RULES.dist): number {
  if (dist === 'uniform') return u;
  const pts = ROLL_CDF[dist];
  for (let i = 1; i < pts.length; i++) {
    const [q0, f0] = pts[i - 1];
    const [q1, f1] = pts[i];
    if (u <= f1) return q0 + ((q1 - q0) * (u - f0)) / (f1 - f0);
  }
  return 1;
}

/** 9. 10. 2026 (owner): an affix rolled at this share of its range or more is marked ("Top roll"). */
export const AFFIX_TOP_ROLL = 0.9;

/** The chance an affix rolls at least `q` of its range (with C: 90% or more is 4%, 1 affix in 25). `dist`: as `rollQuality`. */
export function affixChanceAtLeast(q: number = AFFIX_TOP_ROLL, dist: AffixRollDist = AFFIX_ROLL_RULES.dist): number {
  if (dist === 'uniform') return 1 - q;
  const pts = ROLL_CDF[dist];
  for (let i = 1; i < pts.length; i++) {
    const [q0, f0] = pts[i - 1];
    const [q1, f1] = pts[i];
    if (q <= q1) return 1 - (f0 + ((f1 - f0) * (q - q0)) / (q1 - q0));
  }
  return 0;
}

/** The mean roll quality of the distribution (0.5 when uniform). */
export function affixMeanQuality(): number {
  if (AFFIX_ROLL_RULES.dist === 'uniform') return 0.5;
  const pts = ROLL_CDF[AFFIX_ROLL_RULES.dist];
  let m = 0;
  for (let i = 1; i < pts.length; i++) m += (pts[i][1] - pts[i - 1][1]) * ((pts[i][0] + pts[i - 1][0]) / 2);
  return m;
}

/**
 * One affix from the slot's pool that the item does not have yet (Rework adds one this way, 4. 10. 2026).
 * `attrWeights` (6. 10. 2026, the Relic Forge only): the weight of each attribute inside the attribute row (1 when not
 * given); a drop keeps every attribute equally likely.
 */
export function rollAffix(t: ItemTemplate, ilvl: number, rarity: Rarity, rng: Rng, taken: Set<ItemStatKey>, minRoll = 0, tier?: number | null, attrWeights?: Partial<Record<ItemStatKey, number>>, only?: readonly ItemStatKey[], dist?: AffixRollDist): ItemAffix | null {
  const pool = AFFIX_POOLS[t.slot].filter(([k]) => !taken.has(k) && (!only || only.includes(k)));
  if (!pool.length) return null;
  // One catalog row counts once ("+STR / AGI / INT / VIT / PER" is one row with five stats).
  const rows = [...new Set(pool.map((e) => e[3] ?? e[0]))];
  const row = rng.pick(rows);
  const inRow = pool.filter((e) => (e[3] ?? e[0]) === row);
  const [stat] = row === 'attr' && attrWeights ? rng.weighted(inRow.map((e) => [e, attrWeights[e[0]] ?? 1] as [AffixPoolEntry, number])) : rng.pick(inRow);
  const [lo, hi] = affixRange(t.id, stat, ilvl, rarity, tier)!;
  taken.add(stat);
  // Krug 5 (K4.7): INT lifts the floor of a crafted item's rolls; rounding may not drop under it.
  const floorV = lo + (hi - lo) * clamp(minRoll, 0, 1);
  // 8. 10. 2026 (measurement): the quality's distribution over what is left above INT's floor (the same draw).
  let value = round(floorV + (hi - floorV) * rollQuality(rng.next(), dist), stat);
  if (minRoll > 0 && value < floorV) value = WHOLE.includes(stat) ? Math.ceil(floorV) : Math.ceil(floorV * 100) / 100;
  // Rounding must not lift a catalog affix over its cap (a +4.6 stat stays +4, 6.9% stays 6%).
  if (AFFIX_TIER_RANGES[stat] && value > hi) value = WHOLE.includes(stat) ? Math.floor(hi) : Math.floor(hi * 100) / 100;
  return { stat, value: Math.max(value, WHOLE.includes(stat) ? 1 : 0.01) };
}

export interface GenerateItemOptions {
  templateId?: string;
  slot?: GearSlot;
  /** Only pieces of this set (NPC outfits). */
  setId?: SetId;
  rarity: Rarity;
  ilvl: number;
  slotBias?: Partial<Record<string, number>>;
  /** Krug 5 (K4.7): lowest roll quality of every affix (0–1), for items made in the Workshop. */
  minRoll?: number;
  /** 6. 10. 2026: the Relic Forge's weights of the attributes in the attribute row (`rollAffix`). */
  attrWeights?: Partial<Record<ItemStatKey, number>>;
}

/**
 * The templates `pickTemplate` draws from, with their weights: the item level's tier (one below allowed, half as likely),
 * the slot and set asked for, the slot bias; accessories rarer. Used where there is no zone pool.
 */
export function templatePool(opts: { ilvl: number; slot?: GearSlot; setId?: SetId; slotBias?: Partial<Record<string, number>> }): [ItemTemplate, number][] {
  const tier = tierForLevel(opts.ilvl);
  let pool = ITEM_TEMPLATES.filter((t) => !isSlotLocked(t.slot));
  if (opts.slot) pool = pool.filter((t) => t.slot === opts.slot);
  if (opts.setId) {
    const inSet = pool.filter((t) => t.setId === opts.setId);
    if (inSet.length) return inSet.map((t) => [t, 1]);
  }
  // Prefer the matching tier, allow one tier below.
  const near = pool.filter((t) => t.tier === tier || t.tier === tier - 1);
  if (near.length) pool = near;
  if (!pool.length) pool = ITEM_TEMPLATES.filter((t) => !isSlotLocked(t.slot));
  const weights = pool.map((t) => {
    let w = t.tier === tier ? 2 : 1;
    const bias = opts.slotBias?.[t.slot];
    if (bias) w *= bias;
    // Accessories are rarer than other slots (krug 5, F10.3), unless the slot was asked for.
    if (t.slot === 'accessory' && !opts.slot) w *= ACCESSORY_DROP_WEIGHT;
    return [t, w] as [ItemTemplate, number];
  });
  // 11. 10. 2026 (owner, BACKLOG #36): the item package takes part of its slot's share here too (boxes, rewards, NPC gear).
  return keepSlotShares(weights);
}

/** Picks a template that fits the item level (tier) and slot. Used where there is no zone pool. */
export function pickTemplate(rng: Rng, opts: { ilvl: number; slot?: GearSlot; setId?: SetId; slotBias?: Partial<Record<string, number>> }): ItemTemplate {
  if (opts.setId) {
    // A set asked for (NPC outfits): any of its pieces of the slot, as before.
    const inSet = ITEM_TEMPLATES.filter((t) => !isSlotLocked(t.slot) && (!opts.slot || t.slot === opts.slot) && t.setId === opts.setId);
    if (inSet.length) return rng.pick(inSet);
  }
  return rng.weighted(templatePool({ ...opts, setId: undefined }));
}

let idCounter = 0;
export function newId(prefix = 'it'): string {
  idCounter = (idCounter + 1) % 1_000_000;
  const rand = Math.floor(Math.random() * 0xffffffff).toString(36);
  return `${prefix}_${Date.now().toString(36)}${idCounter.toString(36)}${rand}`;
}

/** Rolls a concrete item: template (given or picked), base stats, affixes and its perk marker. */
export function generateItem(rng: Rng, opts: GenerateItemOptions, id = newId()): ItemInstance {
  const ilvl = clamp(Math.round(opts.ilvl), 1, 20);
  const t = opts.templateId ? itemTemplate(opts.templateId) : pickTemplate(rng, { ilvl, slot: opts.slot, setId: opts.setId, slotBias: opts.slotBias });
  const rarity = opts.rarity;
  const base = scaledBase(t, ilvl, rarity);
  const affixes: ItemAffix[] = [];
  const taken = new Set<ItemStatKey>();
  // 7. 10. 2026 (owner, answer c): a Legendary's extra affix only from the non-combat stats. It is rolled first, so a
  // weapon (whose only non-combat stat is durability) always gets it, and listed last. Other rarities as before.
  const nCombat = combatAffixCount(t.slot, rarity);
  const nonCombat: ItemAffix[] = [];
  for (let i = nCombat; i < affixCount(t.slot, rarity); i++) {
    const a = rollAffix(t, ilvl, rarity, rng, taken, opts.minRoll ?? 0, undefined, opts.attrWeights, NON_COMBAT_AFFIX_STATS);
    if (a) nonCombat.push(a);
  }
  for (let i = 0; i < nCombat; i++) {
    const a = rollAffix(t, ilvl, rarity, rng, taken, opts.minRoll ?? 0, undefined, opts.attrWeights);
    if (a) affixes.push(a);
  }
  affixes.push(...nonCombat);
  // Krug 4: Rare and up carry the item's catalog perks (marked in `passive`); the random
  // pool perk is no longer rolled. Items made before keep whatever perk they have.
  const passive: ItemInstance['passive'] = rollsSignature(t.id, rarity) ? { id: SIGNATURE_PASSIVE_ID } : null;
  // +% max durability (E5): a sturdier item, full when found.
  const extra = affixes.find((a) => a.stat === 'durability')?.value ?? 0;
  const maxDurability = Math.round(MAX_DURABILITY * (1 + extra));
  return { id, templateId: t.id, rarity, ilvl, durability: maxDurability, maxDurability, base, affixes, passive };
}

/**
 * Affixes up to the item's count today, the ones it has kept (krug 5, F10.2: old accessories
 * get the extra affix the new ones roll). Returns the same array when nothing is missing.
 */
export function padAffixes(item: Pick<ItemInstance, 'templateId' | 'ilvl' | 'rarity' | 'affixes' | 'tier'>, rng: Rng): ItemAffix[] {
  const t = itemTemplate(item.templateId);
  const want = affixCount(t.slot, item.rarity);
  if (item.affixes.length >= want) return item.affixes;
  const out = [...item.affixes];
  const taken = new Set<ItemStatKey>(out.map((a) => a.stat));
  while (out.length < want) {
    // A Legendary's extra affix (owner, 7. 10. 2026, answer c): non-combat only, also on Legendaries made before.
    const only = out.length >= combatAffixCount(t.slot, item.rarity) ? NON_COMBAT_AFFIX_STATS : undefined;
    const a = rollAffix(t, item.ilvl, item.rarity, rng, taken, 0, item.tier, undefined, only);
    if (!a) break;
    out.push(a);
  }
  return out;
}

/**
 * How good one affix rolled, 0–1 (E5): where it sits in the range it could have rolled on
 * this item. Old affixes with no range today (flat damage) have none.
 */
export function affixQuality(item: Pick<ItemInstance, 'templateId' | 'ilvl' | 'rarity' | 'tier'>, a: ItemAffix): number | null {
  const r = affixRange(item.templateId, a.stat, item.ilvl, item.rarity, item.tier);
  if (!r) return null;
  const [lo, hi] = r;
  if (hi - lo < 1e-9) return 1;
  return clamp((a.value - lo) / (hi - lo), 0, 1);
}

type RangeItem = Pick<ItemInstance, 'templateId' | 'ilvl' | 'rarity' | 'tier'>;

/**
 * 6. 10. 2026 (owner, rule B, corrected before its deploy): the value an affix gives with its milestone picks (`boost`),
 * before the +N growth of armor and accessories. The first pick gives the more of the top of its range and +10% of its
 * rolled value (an attribute at least +`milestoneAttrMin`, 0 since the owner's decision); each further pick adds 10% of the
 * rolled value; the jump to the top goes at most to 1.2× the top. No pick gives less than the rule before (10% of the rolled value a pick, `old`): where 3–4 picks on a
 * roll near the top would be cut by 1.2×, the rule before stands, so no item is weaker than it was. A Pristine affix (1.3×,
 * over the top already) and an old affix with no range today gain 10% a pick, as before.
 */
export function pickedAffix(item: RangeItem, a: Pick<ItemAffix, 'stat' | 'value' | 'boost' | 'pristine'>): number {
  const picks = a.boost ?? 0;
  const v = a.value;
  if (picks <= 0) return v;
  const old = v * (1 + UPGRADE_RULES.milestoneBoost * picks);
  const r = a.pristine ? null : affixRange(item.templateId, a.stat, item.ilvl, item.rarity, item.tier);
  if (!r) return old;
  const top = r[1];
  const attrFloor = (ATTRS as readonly ItemStatKey[]).includes(a.stat) ? v + UPGRADE_RULES.milestoneAttrMin : v;
  const jump = Math.max(top, attrFloor) + (picks - 1) * UPGRADE_RULES.milestoneBoost * v;
  const cap = Math.max(top * UPGRADE_RULES.milestoneCap, attrFloor);
  return Math.max(old, Math.min(jump, cap));
}

/**
 * 6. 10. 2026 (rule B): the affix a milestone picks when the player did not pick one (an old client, the plan to the
 * item's maximum): the one that grows the most, by its own value (the first of equals).
 */
export function bestPickAffix(item: RangeItem & Pick<ItemInstance, 'affixes'>): number {
  let best = 0;
  let gain = -1;
  item.affixes.forEach((a, i) => {
    const now = pickedAffix(item, a);
    const g = now > 0 ? pickedAffix(item, { ...a, boost: (a.boost ?? 0) + 1 }) / now - 1 : 0;
    if (g > gain + 1e-9) {
      gain = g;
      best = i;
    }
  });
  return best;
}

/**
 * Krug 5 (K4.14): the best roll for its range (the first of equals; affixes with no range count as 0). It was the
 * milestone's pick when the player did not pick one, until 6. 10. 2026 (rule B: `bestPickAffix`).
 */
export function bestRolledAffix(item: Pick<ItemInstance, 'templateId' | 'ilvl' | 'rarity' | 'affixes' | 'tier'>): number {
  let best = 0;
  let q = -1;
  item.affixes.forEach((a, i) => {
    const v = affixQuality(item, a) ?? 0;
    if (v > q) {
      q = v;
      best = i;
    }
  });
  return best;
}

/** Roll quality of an item: the average of its affixes (the catalog's "roll 92%"), or null. */
export function itemRollQuality(item: Pick<ItemInstance, 'templateId' | 'ilvl' | 'rarity' | 'affixes' | 'tier'>): number | null {
  const q = item.affixes.map((a) => affixQuality(item, a)).filter((x): x is number => x != null);
  return q.length ? q.reduce((s, x) => s + x, 0) / q.length : null;
}

/**
 * Sum of base and affix stats. A broken item (0 durability): every stat × 0.75 until it is repaired (owner, 7. 10.
 * 2026; with BROKEN_RULES off, as before: nothing). `ignoreDurability`: the item as it is when whole.
 */
export function itemStats(item: ItemInstance, ignoreDurability = false): ItemStats {
  const broken = !ignoreDurability && item.durability <= 0;
  if (broken && !BROKEN_RULES.on) return {};
  // Upgrades (krug 5, F9): +2% headline stat per level (accessories: every affix), milestone picks on affixes (rule B).
  const out: ItemStats = { ...upgradedBase(item) };
  const m = affixUpgradeMult(item);
  for (const a of item.affixes) out[a.stat] = (out[a.stat] ?? 0) + pickedAffix(item, a) * m;
  if (broken) for (const k of Object.keys(out) as ItemStatKey[]) out[k] = (out[k] ?? 0) * BROKEN_RULES.statMult;
  return out;
}


export function slotOf(item: Pick<ItemInstance, 'templateId'>): GearSlot {
  return itemTemplate(item.templateId).slot;
}

/** Backpack capacity a gear item takes while carried home as loot (not its equipment load). */
export function itemPackSpace(item: Pick<ItemInstance, 'templateId'>): number {
  return PACK_SPACE[slotOf(item)];
}

/** Equipment load of an item (section 7). */
export function itemWeight(item: Pick<ItemInstance, 'templateId'>): number {
  return itemTemplate(item.templateId).weight;
}

type Extra = Partial<Record<'metal' | 'cloth' | 'electronics' | 'chemicals' | 'scrap' | 'relic_shard', number>>;

export function materialFor(slot: GearSlot, repair: boolean): 'metal' | 'cloth' | 'electronics' {
  if (slot === 'weapon' || slot === 'helmet' || (repair && slot === 'boots')) return 'metal';
  if (slot === 'accessory') return 'electronics';
  return 'cloth';
}

/**
 * Scrap and resources returned when an item is dismantled. `bonus` adds scrap only (Salvager,
 * Scrapper 3: 0.1). Krug 5 (K7.2): the materials come from item_salvage.json (items/salvage.ts),
 * in place of the old one material by slot (1 + 2 per rarity step). `accountBound` (Relic Forge)
 * items give no materials.
 */
export function dismantleYield(item: ItemInstance & { accountBound?: boolean; fromTrader?: boolean; crafted?: boolean }, bonus = 0, materialBonus = 0): { scrap: number; extra: StackMap } {
  const r = RARITY_RANK[item.rarity];
  let scrap = Math.round((2 + item.ilvl * 0.8) * [1, 2, 4, 8, 16][r] * (1 + Math.max(0, bonus)));
  // 2. 10. 2026 (package 1, 7.a): a crafted item gives at most 40% of its recipe's Scrap and no Relic Shards.
  const crafted = !!item.crafted && CRAFTED_SALVAGE.on;
  // 4. 10. 2026 (package 3, I): with recipes on the survivor's tier, 40% of the Scrap that rarity costs on the item's tier.
  const cap = crafted ? (RECIPE_RULES.on ? Math.round((gearCraftCost(item.templateId, asCraftRarity(item.rarity), item.tier ?? itemTemplate(item.templateId).tier).scrap ?? 0) * CRAFTED_SALVAGE.scrapShare) : craftedScrapCap(item.templateId, item.rarity)) : null;
  if (cap != null) scrap = Math.min(scrap, cap);
  // Epic gear also gives 1–2 Relic Shards (6.6); never gear bought from the Trader (krug 5, K11.2) or crafted (7.a).
  const shards = item.fromTrader || crafted ? 0 : relicShardsFromDismantle(item);
  // Krug 5 (K7.2): materials by the item's weights (item_salvage.json), at most 40% of its recipe.
  const mats = salvageMaterials(item);
  // 4. 10. 2026 (package 3, G): the Veteran Ledger's Salvager, +5% materials a level.
  if (materialBonus > 0) for (const k of Object.keys(mats) as (keyof StackMap)[]) mats[k] = Math.round((mats[k] ?? 0) * (1 + materialBonus));
  const extra: StackMap = { ...mats, ...(shards ? { relic_shard: shards } : {}) };
  // An upgraded item gives back half of what went into it (krug 5, F9), Scrap included; a tier upgrade too (C4).
  const back = upgradeRefund(item);
  for (const [k, v] of Object.entries(tierUpRefund(item)) as [keyof StackMap, number][]) back[k] = (back[k] ?? 0) + v;
  // 4. 10. 2026 (package 3, H): a reworked item gives back half of what went into its Reworks (never the donors).
  for (const [k, v] of Object.entries(item.rework?.invested ?? {}) as [keyof StackMap, number][]) back[k] = (back[k] ?? 0) + Math.floor(v * REWORK_RULES.refund);
  // 11. 10. 2026 (owner, BACKLOG #36): half of the materials and Scrap its Recalibrates cost, never the Relic Shards.
  for (const [k, v] of Object.entries(item.rework?.recal ?? {}) as [keyof StackMap, number][]) if (k !== 'relic_shard') back[k] = (back[k] ?? 0) + Math.floor(v * RECALIBRATE_REFUND);
  for (const [k, v] of Object.entries(back) as [keyof StackMap, number][]) if (k !== 'scrap') extra[k] = (extra[k] ?? 0) + v;
  // 5. 10. 2026 (owner): an item made in the Workshop stays crafted through its Reworks and tier upgrades and never
  // gives Relic Shards, not even half of the ones a tier upgrade cost (7.a).
  if (crafted) delete extra.relic_shard;
  return { scrap: scrap + (back.scrap ?? 0), extra };
}

/** Cost to repair an item back to full. INT, clan research and the Worker set lower it. */
export function repairCost(item: ItemInstance, discount = 0): { scrap: number; extra: Extra } {
  const missing = item.maxDurability - item.durability;
  if (missing <= 0) return { scrap: 0, extra: {} };
  const r = RARITY_RANK[item.rarity];
  const factor = [1, 1.5, 2.2, 3.2, 4.5][r] * (1 + item.ilvl / 10) * (1 - clamp(discount, 0, 0.6));
  const scrap = Math.max(1, Math.round((missing / 10) * factor));
  const extraQty = Math.round((missing / 25) * (1 + r * 0.5));
  return { scrap, extra: extraQty > 0 ? { [materialFor(slotOf(item), true)]: extraQty } : {} };
}

/**
 * 7. 10. 2026 (owner): the repair of a broken item in the Workshop: the materials of a full repair (with the same
 * discounts), Relic Shards for a Legendary (5; an Epic's 2 went 28. 10. 2026), and the job's minutes by rarity (before
 * the Workshop's speed).
 */
export function brokenRepairCost(item: ItemInstance, discount = 0): { cost: StackMap; minutes: number } {
  const c = repairCost({ ...item, durability: 0 }, discount);
  const cost: StackMap = { scrap: c.scrap, ...(c.extra as StackMap) };
  const shards = BROKEN_REPAIR.shards[item.rarity] ?? 0;
  if (shards) cost.relic_shard = shards;
  return { cost, minutes: BROKEN_REPAIR.minutes[item.rarity] };
}

export function passiveText(id: string): string {
  return PASSIVES[id]?.text ?? '';
}

export function passiveName(id: string): string {
  return PASSIVES[id]?.name ?? id;
}
